import { IOnRoute } from '../crud/types';

import { RecordAccessLevel } from 'data-types';
import type { SearchType } from 'ws-messages';

export interface IRecordFormData {
  id: string;
  user_text: string;
  llm_profile_id: any;
  is_archived?: boolean;
  errors: Record<string, string>;
  uploadedFiles?: string[];
  access_level: RecordAccessLevel;
  retainCurrentAccessLevel?: boolean;
  shareWithSaasTenantIds?: string[];
}

export enum ViewMode {
  SHOW_FORM = 'SHOW_FORM',
  SHOW_CONDENSED_RECORDS_VIEW = 'SHOW_CONDENSED_RECORDS_VIEW',
  SHOW_EXPANDED_VIEW = 'SHOW_EXPANDED_VIEW',
  SHOW_SEARCH = 'SHOW_SEARCH',
}

export interface ILatestPublicRecordSummary {
  id: string;
  subject: string;
  saas_tenant_id: string;
  saas_tenant_name: string;
  created_at: string;
}

export interface ILatestPublicRecord {
  updated_at: string;
  subject: string;
  record_id: string;
  isLinkable: boolean;
  saas_tenant_id: string;
  saas_tenant_name: string;
  summaries: ILatestPublicRecordSummary[];
}

export interface IRecordsState {
  isInitialLoadInProgress: boolean;
  latestPublicRecords: ILatestPublicRecord[];
  records: IRecord[];
  currentRecordId: string | null;
  viewMode: ViewMode;
  recordForm: IRecordFormData;
  showSummaries: boolean;
  showAllLinkable: boolean;
  searchText: string;
  searchType?: SearchType;
  includeArchived: boolean;
  selectedSummariesRecordId: string | null;
  processingStartTimes: { [recordOrSubrecordId: string]: number };
  maximizedRecords: { [recordOrSubrecordId: string]: boolean };
}

/**
 * Represents an record.
 * Extends ICreateRecordParams to include on route status, disabled status, and user ID.
 */
export interface IRecord extends IOnRoute {
  record_id: string;
  subrecordsCount?: number;
  llm_profile_id: string;
  user_text: string;
  ai_text: string; // AI generated
  interim?: boolean | undefined; // true when the backend sent an interim ai response
  subject?: any; // AI generated
  updated_at?: string;
  similarity_score?: number;
  is_archived?: boolean;
  saas_tenant_id?: string;
  saas_tenant_name?: any;
  access_level?: RecordAccessLevel;
  retainCurrentAccessLevel?: boolean;
  shareWithSaasTenantIds?: string[];
  sharedSaasTenantIdsAndNames?: { id: string; name?: string }[];
}

export type ICreateRecordParams = Pick<IRecord, 'record_id' | 'user_text' | 'llm_profile_id' | 'access_level'> & {
  ai_text?: string;
  shareWithSaasTenantIds?: string[];
};

export type ILinkRecordParams = Pick<IRecord, 'record_id' | 'saas_tenant_id'>;

// Record-specific action types
export const RESET_RECORDS = 'RESET_RECORDS';
export const ADD_RECORD = 'ADD_RECORD';
export const APPEND_RECORDS = 'APPEND_RECORDS';
export const SET_CURRENT_RECORD = 'SET_CURRENT_RECORD';
export const SET_RECORD_CONFIRMED_BY_BACKEND = 'SET_RECORD_CONFIRMED_BY_BACKEND';
export const SET_RECORD_STATE = 'SET_RECORD_STATE';
export const DELETE_RECORD = 'DELETE_RECORD';
export const UNLINK_RECORD_TENANT = 'UNLINK_RECORD_TENANT';

export const SET_INITIAL_LOAD_IN_PROGRESS = 'SET_INITIAL_LOAD_IN_PROGRESS';
export const SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES = 'SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES';
export const ADD_LATEST_PUBLIC_RECORDS = 'ADD_LATEST_PUBLIC_RECORDS';
export const UPDATE_LATEST_PUBLIC_RECORD = 'UPDATE_LATEST_PUBLIC_RECORD';
export const REMOVE_LATEST_PUBLIC_RECORD = 'REMOVE_LATEST_PUBLIC_RECORD';
export const SET_VIEW_MODE = 'SET_VIEW_MODE';
export const TOGGLE_EXPAND_CONDENSED_VIEW_MODE = 'TOGGLE_EXPAND_CONDENSED_VIEW_MODE';
export const SET_SHOW_SUMMARIES = 'SET_SHOW_SUMMARIES';
export const SET_SHOW_ALL_LINKABLE = 'SET_SHOW_ALL_LINKABLE';
export const SET_SEARCH_TEXT = 'SET_SEARCH_TEXT';
export const SET_SEARCH_TYPE = 'SET_SEARCH_TYPE';
export const SET_INCLUDE_ARCHIVED = 'SET_INCLUDE_ARCHIVED';
export const SET_SELECTED_SUMMARIES_RECORD_ID = 'SET_SELECTED_SUMMARIES_RECORD_ID';
export const SET_RECORD_AS_NON_LINKABLE = 'SET_RECORD_AS_NON_LINKABLE';
export const TOGGLE_RECORD_MAXIMIZE = 'TOGGLE_RECORD_MAXIMIZE';

// Form-specific action types
export const UPDATE_RECORD_FIELD = 'UPDATE_RECORD_FIELD';
export const SET_RECORD_FORM_ERRORS = 'SET_RECORD_FORM_ERRORS';
export const RESET_RECORD_FORM = 'RESET_RECORD_FORM';

// Record-specific action interfaces
export interface ISetInitialLoadInProgressAction {
  type: typeof SET_INITIAL_LOAD_IN_PROGRESS;
  payload: boolean;
}

export interface IResetRecordsAction {
  type: typeof RESET_RECORDS;
}

export interface IAddRecordAction {
  type: typeof ADD_RECORD;
  payload: IRecord;
}

export interface IAppendRecordsAction {
  type: typeof APPEND_RECORDS;
  payload: IRecord[];
}

export interface ISetCurrentRecordAction {
  type: typeof SET_CURRENT_RECORD;
  payload: string;
}

export interface IRecordConfirmationData {
  user_text?: string; // it's possible that a record was linked without having the user_text during the link, in which case the user_text is updated only when the operation is confirmed by the backend.
  ai_text: string;
  interim?: boolean | undefined; // true when the backend sent an interim ai response
  subject?: string;
  subrecordsCount?: number;
  updated_at: string;
  saas_tenant_id?: string;
  saas_tenant_name?: string;
}

export interface ISetRecordConfirmedByBackendAction {
  type: typeof SET_RECORD_CONFIRMED_BY_BACKEND;
  record_id: string;
  data: IRecordConfirmationData;
}

export interface ISetRecordStateAction {
  type: typeof SET_RECORD_STATE;
  payload: Partial<IRecord> & { record_id: string };
}

export interface IDeleteRecordAction {
  type: typeof DELETE_RECORD;
  payload: string;
}

export interface IUnlinkRecordTenantAction {
  type: typeof UNLINK_RECORD_TENANT;
  payload: {
    record_id: string;
    unlinkedBy: string;
  };
}

export interface ISetViewModeAction {
  type: typeof SET_VIEW_MODE;
  payload: ViewMode;
}

export interface IToggleExpandCondensedViewModeAction {
  type: typeof TOGGLE_EXPAND_CONDENSED_VIEW_MODE;
}

export interface ISetShowSummariesAction {
  type: typeof SET_SHOW_SUMMARIES;
  payload: boolean;
}

export interface ISetShowAllLinkableAction {
  type: typeof SET_SHOW_ALL_LINKABLE;
  payload: boolean;
}

export interface ISetSearchTextAction {
  type: typeof SET_SEARCH_TEXT;
  payload: string;
}

export interface ISetSearchTypeAction {
  type: typeof SET_SEARCH_TYPE;
  payload?: SearchType;
}

export interface ISetIncludeArchivedAction {
  type: typeof SET_INCLUDE_ARCHIVED;
  payload: boolean;
}

export interface ISetLatestPublicRecordsAction {
  type: typeof SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES;
  payload: ILatestPublicRecord[];
}

export interface IAddLatestPublicRecordsAction {
  type: typeof ADD_LATEST_PUBLIC_RECORDS;
  payload: ILatestPublicRecord;
}

export interface IUpdateLatestPublicRecordAction {
  type: typeof UPDATE_LATEST_PUBLIC_RECORD;
  payload: string; // record_id
}

export interface IRemoveLatestPublicRecordAction {
  type: typeof REMOVE_LATEST_PUBLIC_RECORD;
  payload: string; // record_id
}

export interface ISetSelectedSummariesRecordIdAction {
  type: typeof SET_SELECTED_SUMMARIES_RECORD_ID;
  payload: string | null;
}

export interface ISetRecordAsNonLinkableAction {
  type: typeof SET_RECORD_AS_NON_LINKABLE;
  payload: string; // record_id
}

export interface IToggleRecordMaximizeAction {
  type: typeof TOGGLE_RECORD_MAXIMIZE;
  payload: string; // record_id or subrecord_id
}

export interface IUpdateRecordFieldAction {
  type: typeof UPDATE_RECORD_FIELD;
  payload: { field: string; value: string | number | boolean | string[] };
}

export interface ISetRecordFormErrorsAction {
  type: typeof SET_RECORD_FORM_ERRORS;
  payload: Record<string, string>;
}

export interface IResetRecordFormAction {
  type: typeof RESET_RECORD_FORM;
}

export type RecordActionTypes =
  | ISetInitialLoadInProgressAction
  | IResetRecordsAction
  | IAddRecordAction
  | IAppendRecordsAction
  | ISetCurrentRecordAction
  | ISetRecordConfirmedByBackendAction
  | ISetRecordStateAction
  | IDeleteRecordAction
  | IUnlinkRecordTenantAction
  | ISetViewModeAction
  | IToggleExpandCondensedViewModeAction
  | ISetShowSummariesAction
  | ISetShowAllLinkableAction
  | ISetSearchTextAction
  | ISetSearchTypeAction
  | ISetIncludeArchivedAction
  | ISetLatestPublicRecordsAction
  | IAddLatestPublicRecordsAction
  | IUpdateLatestPublicRecordAction
  | IRemoveLatestPublicRecordAction
  | ISetSelectedSummariesRecordIdAction
  | ISetRecordAsNonLinkableAction
  | IToggleRecordMaximizeAction
  | IUpdateRecordFieldAction
  | ISetRecordFormErrorsAction
  | IResetRecordFormAction;
